import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router";
import type { GridFilterItem, GridFilterModel } from "@mui/x-data-grid-pro";
import { produce } from "immer";
import { atom, useAtomValue } from "jotai";
import { RESET } from "jotai/utils";
import { atomWithHash } from "jotai-location";
import { useSearchParamState } from "src/hooks/useSearchParams.ts";
import { generateArraySerializeFns2, generateHashAtomHook } from "@utils/jotai.ts";
import { METRIC_GROUP_TYPE_ALL } from "../components/MetricsTableMetricsFilterButtonGroup/MetricsTableMetricsFilterButtonGroup.constants";
import type { MetricGroupType } from "../components/MetricsTableMetricsFilterButtonGroup/MetricsTableMetricsFilterButtonGroup.types";
import {
  ASSET_PATH_DELIMITER,
  ASSET_PATH_URL_DELIMITER,
  METRIC_GROUP_TYPE_SEARCH_PARAM,
  PATH_FILTER_KEY,
  ROW_SEARCH_PARAM,
} from "../MetricsTabView.constants.ts";
import type { HeaderFilterKey } from "./MetricsTable.constants.ts";
import {
  EMPTY_FILTER_MODEL,
  HEADER_FILTER_KEYS,
  SEARCH_PARAM_OPTS,
  TEST_FILTER_ID,
  TEST_FILTER_ITEMS,
} from "./MetricsTable.constants.ts";
import type { TestFilterOptions } from "./MetricsTable.types.ts";

export type { HeaderFilterKey } from "./MetricsTable.constants.ts";
export type { TestFilterOptions } from "./MetricsTable.types.ts";

const ASSET_FILTER_KEYS = [PATH_FILTER_KEY, "asset_type"] as const;
export type AssetFilterKey = (typeof ASSET_FILTER_KEYS)[number];

type AssetFilterEntry = {
  value: string;
  set: (s: string | typeof RESET) => void;
};

type AssetFiltersMap = { [k in AssetFilterKey]: AssetFilterEntry };

/**
 * Bidirectional codec for an asset filter value at the URL boundary. Lets a
 * key like `path` swap its `|`-separated internal form to a `~`-separated
 * URL form (and back), without leaking that detail to consumers.
 */
type AssetFilterCodec = {
  toUrl: (raw: string) => string;
  fromUrl: (urlValue: string) => string;
};

/**
 * `path` is the only asset filter that contains the hierarchy delimiter.
 * `asset_type` is a single token (`pipeline`, `task`, `tf`, ...) and needs
 * no transformation, so it omits a codec.
 *
 * Read direction is intentionally lenient: only `~` rewrites to `|`. A literal
 * `|` (i.e. `%7C`) in an existing URL — for example a deep-link bookmarked
 * before this change — round-trips unchanged.
 */
const assetPathFilterCodec: AssetFilterCodec = {
  toUrl: (raw) => raw.replaceAll(ASSET_PATH_DELIMITER, ASSET_PATH_URL_DELIMITER),
  fromUrl: (urlValue) =>
    urlValue.replaceAll(ASSET_PATH_URL_DELIMITER, ASSET_PATH_DELIMITER),
};

/**
 * URL `?path=` value from internal `|`-delimited hierarchy (see `ASSET_PATH_URL_DELIMITER`).
 *
 * Only this encode leg is exported (not the whole `assetPathFilterCodec`): callers outside this
 * module that patch `location.search` in bulk (e.g. `useSetAssetPathClearingRow`) need the
 * same URL encoding as `useAssetFilterParam`, but should not depend on `fromUrl` or on a shared
 * mutable codec object — that would widen the public API and invite decode/encode drift at call
 * sites that only care about writing `path=`.
 */
export const encodeMetricAssetPathSearchParamValue = assetPathFilterCodec.toUrl;

/**
 * Sets encoded `?path=` from internal `|`-delimited hierarchy and always removes `?row=`
 * in one `setSearchParams` update so `useSelectFirstRow` can re-bootstrap selection.
 */
export const useSetAssetPathClearingRow = () => {
  const [, setSearchParams] = useSearchParams();

  return useCallback(
    (internalPathRaw: string | null | undefined) => {
      const internalPath = internalPathRaw ?? "";
      setSearchParams(
        (prev) => {
          const next = Object.fromEntries(prev.entries());
          delete next[ROW_SEARCH_PARAM];
          if (internalPath) {
            next[PATH_FILTER_KEY] =
              encodeMetricAssetPathSearchParamValue(internalPath);
          } else {
            delete next[PATH_FILTER_KEY];
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );
};

const useAssetFilterParam = (
  key: AssetFilterKey,
  codec?: AssetFilterCodec
): AssetFilterEntry => {
  const { value, setSearchParam } = useSearchParamState<string | null>(
    key,
    null,
    SEARCH_PARAM_OPTS
  );

  return useMemo(
    () => ({
      value: !value ? "" : codec ? codec.fromUrl(value) : value,
      // `setSearchParam("")` / `null` removes the param (see `useSearchParamState`).
      // RESET maps to "remove" — same observable URL behaviour as the old
      // `atomWithHash(... , "", { setHash: "replaceState" })` reset.
      set: (s: string | typeof RESET) => {
        if (s === RESET || !s) {
          setSearchParam(null);
          return;
        }
        setSearchParam(codec ? codec.toUrl(s) : s);
      },
    }),
    [value, setSearchParam, codec]
  );
};

export const useAssetFilters = (): AssetFiltersMap => {
  const path = useAssetFilterParam(PATH_FILTER_KEY, assetPathFilterCodec);
  const asset_type = useAssetFilterParam("asset_type");

  return useMemo(() => ({ path, asset_type }), [path, asset_type]);
};

export const useQuickFilter = generateHashAtomHook(
  "quickFilter",
  [] as string[],
  generateArraySerializeFns2<string[]>()
);

export const quickFilterHashAtom = useQuickFilter.atom;

export const testsFilterHashAtom = atomWithHash<TestFilterOptions>(
  "testsFilter",
  "",
  {
    setHash: "replaceState",
  }
);

export const headerFilterAtoms = Object.fromEntries(
  HEADER_FILTER_KEYS.map((key) => [
    key,
    atomWithHash<HeaderFilterKey | null>(key, null, {
      setHash: "replaceState",
    }),
  ])
) as {
  [key in HeaderFilterKey]: ReturnType<typeof atomWithHash<string>>;
};

export const headerFiltersIndexAtom = atom((get) => {
  return Object.fromEntries(
    Object.entries(headerFilterAtoms)
      .map(([k, a]) => [k, get(a)])
      .filter(([, v]) => !!v)
  );
});

export const resetHeaderFiltersAtom = atom(null, (_get, set) => {
  for (const a of Object.values(headerFilterAtoms)) {
    set(a, RESET);
  }
});

export const useMetricGroupTypeFilter = () =>
  useSearchParamState<MetricGroupType | null>(
    METRIC_GROUP_TYPE_SEARCH_PARAM,
    METRIC_GROUP_TYPE_ALL,
    SEARCH_PARAM_OPTS
  );

const upsertHeaderFilterItem = (
  filterModel: GridFilterModel,
  field: string,
  value: string | null
) =>
  produce(filterModel, (draft) => {
    const filterId = `${field}_from_header`;
    const i = draft.items.findIndex((f) => f.id === filterId);

    if (!value) {
      if (i !== -1) draft.items.splice(i, 1);
      return;
    }

    const item: GridFilterItem = {
      id: filterId,
      field,
      value,
      operator: "equals",
    };
    if (i !== -1) draft.items[i] = item;
    else draft.items.push(item);
  });

const upsertTestFilterItem = (
  filterModel: GridFilterModel,
  value: TestFilterOptions
) =>
  produce(filterModel, (draft) => {
    const i = draft.items.findIndex((f) => f.id === TEST_FILTER_ID);

    if (value === "") {
      if (i !== -1) draft.items.splice(i, 1);
      return;
    }

    const item = TEST_FILTER_ITEMS[value];
    if (i !== -1) draft.items[i] = item;
    else draft.items.push(item);
  });

/**
 * `field: key` is intentional: see role (3) on `ASSET_FILTER_KEYS`. The DataGrid
 * `startsWith` operator resolves to `row[field]`, so the filter field name must
 * stay equal to the matching property on the metrics row (e.g. `row.path`,
 * `row.asset_type`). The `id` (`asset-tree-filter--${key}`) is just a stable
 * upsert handle so this filter slot gets replaced, not duplicated, when the
 * tree selection changes.
 */
const upsertAssetFilterItem = (
  filterModel: GridFilterModel,
  key: AssetFilterKey,
  value: string
) =>
  produce(filterModel, (draft) => {
    const filterId = `asset-tree-filter--${key}`;
    const i = draft.items.findIndex((f) => f.id === filterId);

    if (!value) {
      if (i !== -1) draft.items.splice(i, 1);
      return;
    }

    const item: GridFilterItem = {
      id: filterId,
      field: key,
      value,
      operator: "startsWith",
    };
    if (i !== -1) draft.items[i] = item;
    else draft.items.push(item);
  });

/**
 * Composes the DataGrid `filterModel` from the various filter sources. Was previously
 * a derived jotai atom (`readFiltersAtom`); migrated to a hook because two of its
 * inputs — `path`, `asset_type`, and `metricGroupType` — live in React Router
 * URL search params and are therefore not readable via `get(atom)`. Remaining
 * inputs are still jotai atoms and are read via `useAtomValue`.
 */
export const useReadFilters = (): GridFilterModel => {
  const headerFilterIndex = useAtomValue(headerFiltersIndexAtom);
  const testsFilter = useAtomValue(testsFilterHashAtom);
  const quickFilter = useAtomValue(quickFilterHashAtom);
  const [metricGroupType] = useMetricGroupTypeFilter();
  const assetFilters = useAssetFilters();

  return useMemo(() => {
    let model: GridFilterModel = EMPTY_FILTER_MODEL;

    for (const [field, value] of Object.entries(headerFilterIndex)) {
      if (value) model = upsertHeaderFilterItem(model, field, value as string);
    }

    model = upsertTestFilterItem(model, testsFilter);

    model = produce(model, (draft) => {
      draft.quickFilterValues = quickFilter;
    });

    for (const key of ASSET_FILTER_KEYS) {
      model = upsertAssetFilterItem(model, key, assetFilters[key].value);
    }

    if (metricGroupType && metricGroupType !== METRIC_GROUP_TYPE_ALL) {
      model = produce(model, (draft) => {
        draft.items.push({
          id: "metricGroupType",
          field: "metric_group_type",
          value: metricGroupType,
          operator: "equals",
        });
      });
    }

    return model;
  }, [
    headerFilterIndex,
    testsFilter,
    quickFilter,
    metricGroupType,
    assetFilters.path.value,
    assetFilters.asset_type.value,
  ]);
};
